import { Feature, GeoJsonProperties, Geometry } from "geojson";
import { FunctionComponent, useCallback, useMemo, useState } from "react";
import { Marker as MapGLMarker } from "react-map-gl/mapbox";

import {
  MapMarker,
  MapMarkerValue,
  MapMarkerValueColor,
  MapMarkerWrapper,
} from "@/features/shared/widgets/map/map.styles";
import { SeismicEventPropertyOptions } from "@/features/shared/widgets/map/map.types";
import getSeismicColors from "@/features/shared/widgets/map/layers/seismic-events-layer/get-seismic-colors";
import { LayerSettingValueSet } from "@/lib/map-layers/layer-settings";

interface SeismicEventsMarkerProps {
  feature: Feature<Geometry, GeoJsonProperties>;
  selected: boolean;
  onSelect: (feature: Feature<Geometry, GeoJsonProperties>) => void;
  currentSettingValues: LayerSettingValueSet;
}

const SeismicEventsMarker: FunctionComponent<SeismicEventsMarkerProps> = ({
  feature,
  selected,
  onSelect,
  currentSettingValues,
}) => {
  const [hovered, setHovered] = useState(false);

  const handleMarkerClick = useCallback(
    (feature: Feature<Geometry, GeoJsonProperties>) => () => {
      onSelect(feature);
    },
    [onSelect],
  );

  const backgroundColor = useMemo(() => {
    if (!feature.properties) return undefined;

    const selectedProperty =
      (currentSettingValues?.["seismicEventsType"]?.value as SeismicEventPropertyOptions) ??
      SeismicEventPropertyOptions.AGE;

    return getSeismicColors(
      selectedProperty,
      feature.properties?.time,
      feature.properties?.depth,
    );
  }, [feature, currentSettingValues]);

  const state = useMemo(() => {
    if (selected) return "active";
    if (hovered) return "hover";
    return undefined;
  }, [selected, hovered]);

  return (
    <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <MapGLMarker
        latitude={feature.properties?.lat}
        longitude={feature.properties?.lon}
        onClick={handleMarkerClick(feature)}
        anchor="bottom"
        style={{
          zIndex: state ? 500 : 100,
        }}
      >
        <MapMarkerWrapper>
          <MapMarker state={state}>
            <MapMarkerValue state={state}>
              <MapMarkerValueColor style={{ backgroundColor }}></MapMarkerValueColor>
            </MapMarkerValue>
            {feature.properties?.mag && (
              <MapMarkerValue>{feature.properties.mag.toFixed(1)}</MapMarkerValue>
            )}
          </MapMarker>
        </MapMarkerWrapper>
      </MapGLMarker>
    </div>
  );
};

export default SeismicEventsMarker;
